<template>
  <div class="container">
    <div class="box-title" style="display:flex;justify-content: space-between">
      <h4>
        <i class="fal fa-user-check"></i>
        {{ $t('deliveryProofSenders.page_title') }}
        <span class="countTotal" v-if="openTotal">({{ openTotal }})</span>
      </h4>
      <div class="header-actions">
        <button class="button" @click="$router.push({ name: 'delivery.proof.queue' })">
          {{ $t('deliveryProofSenders.go_to_queue') }}
        </button>
        <button class="button is-info" @click="toggleEnableForm">
          {{ showEnableForm ? $t('deliveryProofSenders.cancel') : $t('deliveryProofSenders.enable_new_sender') }}
        </button>
      </div>
    </div>

    <!-- The feature is CLOSED by default. Said on the screen, because an empty
         list here and a broken feature look identical from the queue. -->
    <div class="box notice">
      {{ $t('deliveryProofSenders.closed_by_default_notice') }}
    </div>

    <!-- ── open a sender ──────────────────────────────────────────────── -->
    <div class="box" v-if="showEnableForm">
      <div class="columns is-multiline">
        <div class="column is-8">
          <label class="label">{{ $t('deliveryProofSenders.search_cod_customer_label') }}</label>
          <!-- No phone-number FIELD: the number is never typed, it comes from
               the record that is picked. The endpoint refuses a typed one. -->
          <input
            class="input"
            type="text"
            v-model="searchQuery"
            :placeholder="$t('deliveryProofSenders.search_placeholder')"
            @keyup.enter="searchCustomers"
          />
          <!-- Prefix search over COD-approved customers only: a substring
               search over every customer took about a minute. -->
          <p class="search-hint">{{ $t('deliveryProofSenders.search_hint') }}</p>
        </div>
        <div class="column is-4" style="display:flex;align-items:flex-end">
          <button class="button is-primary" :disabled="searching" @click="searchCustomers">
            {{ searching ? $t('deliveryProofSenders.searching') : $t('deliveryProofSenders.search') }}
          </button>
        </div>
      </div>

      <p v-if="searchError" class="report-error">{{ searchError }}</p>

      <!-- Said while the request is in flight. Without it a slow search looks
           like a dead button, and the operator presses it again. -->
      <p v-if="searching" class="searching-line">
        <i class="fal fa-spinner-third fa-spin"></i> {{ $t('deliveryProofSenders.searching_customers') }}
      </p>

      <vs-table v-if="customers.length">
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('deliveryProofSenders.name') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.tel') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.customer_code') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.customer_type') }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr
            v-for="row in customers"
            :key="row.id"
            class="clickable"
            :class="{ picked: selected && selected.id === row.id }"
            @click.native="selectCustomer(row)"
          >
            <vs-td>{{ fullName(row) }}</vs-td>
            <vs-td>{{ row.tel || '—' }}</vs-td>
            <vs-td>{{ row.customer_id || '—' }}</vs-td>
            <vs-td>{{ row.customer_type || '—' }}</vs-td>
            <vs-td>
              <span v-if="selected && selected.id === row.id"><i class="fal fa-check"></i></span>
              <span v-else-if="row.open_period_id" class="state-open">{{ $t('deliveryProofSenders.already_open') }}</span>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-else-if="searched && !searching" class="empty-line">{{ $t('deliveryProofSenders.no_customers_found') }}</p>

      <!-- ── the preview, which is a required step ──────────────────────
           Not a button someone may press: the confirm below stays disabled
           until this has been fetched, and picking another record throws it
           away again. It is the only place the review load about to arrive is
           visible BEFORE it arrives. -->
      <div class="preview" v-if="selected">
        <div class="preview-head">
          <strong>{{ fullName(selected) }}</strong>
          <span class="tel">{{ preview ? preview.sender_tel : (selected.tel || '—') }}</span>
          <button class="button is-small" :disabled="previewing" @click="fetchPreview">
            {{ preview ? $t('deliveryProofSenders.reload_preview') : $t('deliveryProofSenders.preview_before_enable') }}
          </button>
        </div>

        <p v-if="previewError" class="report-error">{{ previewError }}</p>

        <div v-if="preview">
          <p class="preview-note">
            {{ $t('deliveryProofSenders.preview_note_before') }} <strong>{{ $t('deliveryProofSenders.tel') }}</strong>
            {{ $t('deliveryProofSenders.preview_note_after') }}
          </p>

          <vs-table>
            <template #thead>
              <vs-tr>
                <vs-th>{{ $t('deliveryProofSenders.customer_record') }}</vs-th>
                <vs-th>{{ $t('deliveryProofSenders.customer_type') }}</vs-th>
                <vs-th>{{ $t('deliveryProofSenders.bills_30d') }}</vs-th>
              </vs-tr>
            </template>
            <template #tbody>
              <vs-tr v-for="row in preview.customers" :key="row.id">
                <vs-td>{{ row.name }} <small>#{{ row.id }}</small></vs-td>
                <vs-td>{{ row.customer_type }}</vs-td>
                <vs-td>{{ row.despatched_30d }}</vs-td>
              </vs-tr>
            </template>
          </vs-table>

          <p class="preview-total">
            {{ $t('deliveryProofSenders.total_bills_30d_label') }} <strong>{{ preview.despatched_30d_total }}</strong>
            {{ $t('deliveryProofSenders.total_bills_30d_hint') }}
          </p>

          <p v-if="preview.already_open_period_id" class="already-open">
            {{ $t('deliveryProofSenders.sender_already_open', { id: preview.already_open_period_id }) }}
          </p>

          <div class="columns is-multiline" v-else>
            <div class="column is-8">
              <label class="label">{{ $t('deliveryProofSenders.note_optional') }}</label>
              <input class="input" type="text" v-model="note" :placeholder="$t('deliveryProofSenders.note_placeholder')" />
            </div>
            <div class="column is-4" style="display:flex;align-items:flex-end">
              <button
                class="button is-primary"
                :disabled="!canConfirm"
                @click="enableSender"
              >
                {{ $t('deliveryProofSenders.confirm_enable') }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <p v-if="enableError" class="report-error">{{ enableError }}</p>
    </div>

    <!-- ── the periods ───────────────────────────────────────────────────
         Withdrawn periods are listed WITH the open ones. The backend keeps a
         closed period because the record of who authorised the review load is
         the point of keeping it; hiding them by default would undo that at the
         one place a person reads it. -->
    <div class="columns list-search">
      <div class="column is-6">
        <input
          class="input"
          type="text"
          v-model="listQuery"
          :placeholder="$t('deliveryProofSenders.list_search_placeholder')"
          @keyup.enter="fetchSenders(1)"
        />
      </div>
    </div>

    <!-- Filtered on the SERVER: with every COD sender enabled the list runs to
         tens of thousands of periods, so a filter over one page would lie. -->
    <div class="tabs">
      <ul>
        <li v-for="tab in tabs" :key="tab.key" :class="{ 'is-active': filter === tab.key }">
          <a @click="setFilter(tab.key)">{{ tab.label }}</a>
        </li>
      </ul>
    </div>

    <p v-if="lastReportError" class="report-error">{{ lastReportError }}</p>

    <div class="center">
      <vs-table>
        <template #thead>
          <vs-tr>
            <vs-th>{{ $t('deliveryProofSenders.sender_tel') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.name') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.bills_covered') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.bills_signed') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.bills_awaiting_sign') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.enabled_by_at') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.disabled_by_at') }}</vs-th>
            <vs-th>{{ $t('deliveryProofSenders.status') }}</vs-th>
            <vs-th></vs-th>
          </vs-tr>
        </template>
        <template #tbody>
          <vs-tr v-for="row in rows" :key="row.id">
            <vs-td><strong>{{ row.sender_tel }}</strong></vs-td>
            <vs-td>{{ row.display_name || '—' }}</vs-td>
            <!-- Zero here is the ONLY visible symptom of the wrong record
                 having been picked: everywhere else an enablement that covers
                 nothing looks exactly like no enablement. -->
            <!-- Counted on request, one row at a time: the list no longer
                 carries the counts (they took the page to a minute). -->
            <vs-td :class="{ 'covers-nothing': coversNothing(row) }">
              <template v-if="countsOf(row)">
                {{ countsOf(row).covered }}
                <span v-if="coversNothing(row)" class="covers-hint">
                  {{ $t('deliveryProofSenders.covers_nothing_hint') }}
                </span>
              </template>
              <button
                v-else
                class="button is-small"
                :disabled="!!countsLoading[row.id]"
                @click="loadCounts(row)"
              >
                {{ countsLabel(row) }}
              </button>
            </vs-td>
            <!-- Closed — by the branch, by head office on its behalf, or by
                 auto-sign. The server counts it; this only renders it. -->
            <vs-td class="count-signed">{{ countsOf(row) ? displayCount(countsOf(row).signed) : '—' }}</vs-td>
            <!-- Sitting at the destination branch, unkeyed. The number this
                 whole line of work exists to shrink. -->
            <vs-td :class="{ 'count-awaiting': countsOf(row) && (countsOf(row).awaiting || 0) > 0 }">
              {{ countsOf(row) ? displayCount(countsOf(row).awaiting) : '—' }}
            </vs-td>
            <vs-td>{{ actorLine(row.enabled_by, row.enabled_at) }}</vs-td>
            <vs-td>{{ row.disabled_at ? actorLine(row.disabled_by, row.disabled_at) : '—' }}</vs-td>
            <vs-td>
              <span :class="row.is_open ? 'state-open' : 'state-closed'">
                {{ row.is_open ? $t('deliveryProofSenders.state_open') : $t('deliveryProofSenders.state_closed') }}
              </span>
            </vs-td>
            <vs-td>
              <!-- Open periods can be closed. A closed one offers nothing:
                   there is no edit and no delete on this line at all. -->
              <button v-if="row.is_open" class="button is-small" @click="askDisable(row)">
                {{ $t('deliveryProofSenders.disable') }}
              </button>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>

      <p v-if="!loading && rows.length === 0" class="empty-line">
        {{ $t('deliveryProofSenders.empty_state') }}
      </p>

      <div v-if="lastPage > 1" class="pager">
        <button class="button" :disabled="page <= 1" @click="fetchSenders(page - 1)">{{ $t('deliveryProofSenders.prev_page') }}</button>
        <span>{{ page }} / {{ lastPage }}</span>
        <button class="button" :disabled="page >= lastPage" @click="fetchSenders(page + 1)">{{ $t('deliveryProofSenders.next_page') }}</button>
      </div>
    </div>

    <!-- ── withdrawing ──────────────────────────────────────────────────
         The consequence is stated BEFORE (bills already despatched keep their
         coverage) and the size of it AFTER (`bills_still_covered`). Without the
         first, head office expects the queue to stop; without the second, the
         submissions that keep coming read as a bug. -->
    <div class="confirm-card" v-if="disabling">
      <h5>{{ $t('deliveryProofSenders.disable_sender_title', { tel: disabling.sender_tel }) }}</h5>
      <p>
        {{ $t('deliveryProofSenders.disable_warning_before') }} <strong>{{ $t('deliveryProofSenders.disable_warning_strong') }}</strong>
        {{ $t('deliveryProofSenders.disable_warning_after') }}
      </p>
      <div class="columns is-multiline">
        <div class="column is-8">
          <label class="label">{{ $t('deliveryProofSenders.note_optional') }}</label>
          <input class="input" type="text" v-model="disableNote" />
        </div>
      </div>
      <div class="confirm-actions">
        <button class="button" @click="disabling = null">{{ $t('deliveryProofSenders.cancel') }}</button>
        <button class="button is-danger" :disabled="disablingBusy" @click="confirmDisable">{{ $t('deliveryProofSenders.confirm_disable') }}</button>
      </div>
    </div>

    <div class="confirm-card result" v-if="disableResult">
      <p>
        {{ $t('deliveryProofSenders.disabled_result_before') }} <strong>{{ disableResult.bills_still_covered }}</strong>
        {{ $t('deliveryProofSenders.disabled_result_after') }}
      </p>
      <!-- The same number enrolled under another spelling is withdrawn with it. -->
      <p v-if="disableResult.also_closed_period_ids && disableResult.also_closed_period_ids.length">
        {{ $t('deliveryProofSenders.also_closed_before') }} <strong>{{ disableResult.also_closed_period_ids.length }}</strong> {{ $t('deliveryProofSenders.also_closed_after') }}
      </p>
      <button class="button" @click="disableResult = null">{{ $t('deliveryProofSenders.acknowledge') }}</button>
    </div>
  </div>
</template>

<script>
import DeliveryProofApi from '@/Mixins/DeliveryProof/DeliveryProofApi';

/**
 * Which senders proof of delivery is open for.
 *
 * The only screen in this repo whose effect is invisible on every other screen:
 * enable the wrong customer record and nothing appears anywhere — no error, no
 * empty state, no difference from having done nothing at all. Most of what this
 * component does beyond listing rows exists to make that invisibility visible —
 * the required preview, and the zero-coverage warning on the list.
 *
 * Nothing here recalculates a figure. Counts arrive computed and are rendered.
 *
 * @see openspec/changes/add-delivery-proof-sender-allowlist/design.md
 */
export default {
    name: 'DeliveryProofSenders',
    mixins: [DeliveryProofApi],

    data() {
        return {
            rows: [],
            filter: 'all',
            listQuery: '',
            openTotal: 0,
            page: 1,
            lastPage: 1,
            loading: false,

            /** The latest list request; older answers are dropped. */
            listSeq: 0,

            /** Per-period counts fetched on request, keyed by period id. */
            counts: {},
            countsLoading: {},

            showEnableForm: false,
            searchQuery: '',
            customers: [],
            searching: false,
            searched: false,
            searchError: null,

            selected: null,
            preview: null,
            previewing: false,
            previewError: null,

            note: '',
            enableError: null,

            disabling: null,
            disableNote: '',
            disablingBusy: false,
            disableResult: null,

            tabs: [
                { key: 'all', label: this.$t('deliveryProofSenders.all') },
                { key: 'open', label: this.$t('deliveryProofSenders.state_open') },
                { key: 'closed', label: this.$t('deliveryProofSenders.state_closed') },
            ],
        };
    },

    computed: {
        /**
         * Enabling is a two-step flow by construction: a record must be picked
         * AND its coverage previewed. Picking another record clears the preview,
         * which disables this again.
         */
        canConfirm() {
            return !!this.selected && !!this.preview && !this.preview.already_open_period_id;
        },
    },

    mounted() {
        this.fetchSenders(1);
    },

    methods: {
        fetchSenders(page) {
            // Only the LATEST request may write the list: switching tabs or
            // searching again while one is in flight must not let an older
            // answer land last and show the wrong filter's rows.
            const seq = ++this.listSeq;
            this.loading = true;
            this.lastReportError = null;
            // Every list load starts without counts — they are re-fetched on
            // request (cheap: the server caches them 10 min), so a period closed
            // by someone else never keeps showing what it covered before.
            this.counts = {};
            this.countsLoading = {};

            const params = { page, limit: 50 };
            if (this.filter !== 'all') params.status = this.filter;
            if (this.listQuery.trim()) params.q = this.listQuery.trim();

            this.deliveryProofSenders(params)
                .then(({ data }) => {
                    if (seq !== this.listSeq) return;
                    const payload = data.data;
                    // Closing the only row of the last page empties that page:
                    // step back to the last page that still has rows.
                    const lastPage = payload.last_page || 1;
                    if ((payload.current_page || 1) > lastPage) {
                        this.fetchSenders(lastPage);
                        return;
                    }
                    this.rows = payload.items || [];
                    this.page = payload.current_page || 1;
                    this.lastPage = payload.last_page || 1;
                    this.openTotal = payload.open_total != null
                        ? payload.open_total
                        : this.rows.filter((r) => r.is_open).length;
                })
                .catch((error) => {
                    if (seq !== this.listSeq) return;
                    this.rows = [];
                    this.handleReportError(error);
                })
                .finally(() => {
                    if (seq === this.listSeq) this.loading = false;
                });
        },

        /** Re-clicking the active tab reloads it. */
        setFilter(key) {
            this.filter = key;
            this.fetchSenders(1);
        },

        /**
         * One row's counts. Cached 10 min on the server, and a very large
         * sender answers `timed_out` instead of holding the database.
         */
        loadCounts(row) {
            // An answer that arrives after the list reloaded belongs to the old
            // list (the period may have been closed since) — drop it.
            const seq = this.listSeq;
            this.$set(this.countsLoading, row.id, true);

            this.deliveryProofSenderCounts(row.id)
                .then(({ data }) => {
                    if (seq === this.listSeq) this.$set(this.counts, row.id, data.data);
                })
                .catch((error) => {
                    if (seq === this.listSeq) this.handleReportError(error);
                })
                .finally(() => {
                    if (seq === this.listSeq) this.$set(this.countsLoading, row.id, false);
                });
        },

        /** The row's counts once known — null until then (the button shows). */
        countsOf(row) {
            const fetched = this.counts[row.id];
            if (fetched && fetched.state === 'ok') return fetched;
            // An older backend still sends them inline.
            if (row.bills_covered !== null && row.bills_covered !== undefined) {
                return { covered: row.bills_covered, signed: row.bills_signed, awaiting: row.bills_awaiting_sign };
            }
            return null;
        },

        countsLabel(row) {
            if (this.countsLoading[row.id]) return this.$t('deliveryProofSenders.counting');
            const fetched = this.counts[row.id];
            if (fetched && fetched.state === 'busy') return this.$t('deliveryProofSenders.counting_busy_retry');
            if (fetched && fetched.state === 'timed_out') return this.$t('deliveryProofSenders.counting_timed_out');
            return this.$t('deliveryProofSenders.show_counts');
        },

        toggleEnableForm() {
            this.showEnableForm = !this.showEnableForm;
            if (!this.showEnableForm) this.resetEnableForm();
        },

        resetEnableForm() {
            this.searchQuery = '';
            this.customers = [];
            this.searched = false;
            this.searchError = null;
            this.clearSelection();
            this.note = '';
            this.enableError = null;
        },

        clearSelection() {
            this.selected = null;
            this.preview = null;
            this.previewError = null;
        },

        searchCustomers() {
            if (!this.searchQuery) return;

            this.searching = true;
            this.searchError = null;
            this.clearSelection();

            this.searchCustomersForSender(this.searchQuery)
                .then(({ data }) => {
                    this.customers = (data.data && data.data.items) || [];
                    this.searched = true;
                })
                .catch((error) => {
                    this.customers = [];
                    this.searchError = error?.response?.data?.message || this.$t('deliveryProofSenders.error_search');
                })
                .finally(() => {
                    this.searching = false;
                });
        },

        /** Picking a different record throws the previous preview away. */
        selectCustomer(row) {
            this.selected = row;
            this.preview = null;
            this.previewError = null;
            this.enableError = null;
        },

        fetchPreview() {
            this.previewing = true;
            this.previewError = null;

            this.deliveryProofSenderPreview(this.selected.id)
                .then(({ data }) => {
                    this.preview = data.data;
                })
                .catch((error) => {
                    this.preview = null;
                    this.previewError = error?.response?.data?.message || this.$t('deliveryProofSenders.error_preview');
                })
                .finally(() => {
                    this.previewing = false;
                });
        },

        enableSender() {
            this.enableError = null;

            this.deliveryProofSenderEnable({
                customer_id: this.selected.id,
                note: this.note || null,
            })
                .then(() => {
                    this.resetEnableForm();
                    this.showEnableForm = false;
                    this.fetchSenders(1);
                })
                .catch((error) => {
                    // An "already open" refusal is a normal answer, not an
                    // alarm — so the server's own wording is what is shown.
                    this.enableError = error?.response?.data?.message || this.$t('deliveryProofSenders.error_enable');
                });
        },

        askDisable(row) {
            this.disabling = row;
            this.disableNote = '';
            this.disableResult = null;
        },

        confirmDisable() {
            this.disablingBusy = true;

            const periodId = this.disabling.id;

            this.deliveryProofSenderDisable(periodId, { note: this.disableNote || null })
                .then(({ data }) => {
                    // Closing changes what the period covers; drop its counts.
                    this.$delete(this.counts, periodId);
                    this.disableResult = data.data;
                    this.disabling = null;
                    this.fetchSenders(this.page);
                })
                .catch((error) => {
                    this.handleReportError(error);
                    this.disabling = null;
                })
                .finally(() => {
                    this.disablingBusy = false;
                });
        },

        /**
         * A period opened TODAY that covers nothing is not evidence of anything
         * — the sender may simply not have despatched yet. The floor is what
         * keeps the warning meaningful.
         */
        coversNothing(row) {
            const counts = this.countsOf(row);
            if (!counts || counts.covered === null || counts.covered === undefined) return false;
            if (counts.covered > 0) return false;

            return Date.now() - new Date(row.enabled_at).getTime() > 86400000;
        },

        /**
         * A count the server did not send (an older backend) reads as unknown,
         * never as zero — zero here would claim every bill is signed.
         */
        displayCount(value) {
            return (value === null || value === undefined) ? '—' : value;
        },

        fullName(row) {
            return [row.customer_name || row.name, row.customer_surname || row.surname].filter(Boolean).join(' ')
                || '—';
        },

        actorLine(actor, at) {
            const who = actor?.name || (actor?.id ? `#${actor.id}` : '—');
            return `${who} · ${this.shortDateTime(at)}`;
        },

        shortDateTime(value) {
            if (!value) return '—';
            const d = new Date(value);
            return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.header-actions {
    display: flex;
    gap: 8px;
}

.notice {
    color: #555;
}

.report-error {
    margin-bottom: 12px;
    padding: 10px;
    background: #fdecea;
    color: #b71c1c;
    border-radius: 4px;
}

.clickable {
    cursor: pointer;
}

.picked {
    background: #eaf2ff;
}

.preview {
    margin-top: 16px;
    padding: 12px;
    border: 1px solid #dde3ec;
    border-radius: 4px;
}

.preview-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    .tel {
        color: #666;
    }
}

.preview-note {
    margin-bottom: 8px;
    color: #555;
}

.preview-total {
    margin-top: 8px;
}

.already-open {
    margin-top: 8px;
    padding: 8px 10px;
    background: #fff6e5;
    color: #8a5a00;
    border-radius: 4px;
}

.search-hint {
    margin-top: 4px;
    font-size: 12px;
    color: #777;
}

.list-search {
    margin-top: 8px;
}

.searching-line {
    padding: 10px 0;
    color: #555;
}

.count-signed {
    color: #1b7f3b;
}

.count-awaiting {
    color: #c77700;
    font-weight: 600;
}

.covers-nothing {
    color: #b71c1c;
    font-weight: 600;
}

.covers-hint {
    display: block;
    font-weight: 400;
    font-size: 12px;
}

.state-open {
    color: #1b7f3b;
    font-weight: 600;
}

.state-closed {
    color: #777;
}

.empty-line {
    padding: 24px 0;
    text-align: center;
    color: #666;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 12px;
}

.confirm-card {
    margin-top: 16px;
    padding: 14px;
    border: 1px solid #f0c9c9;
    border-radius: 4px;
    background: #fff9f9;

    &.result {
        border-color: #cfe6d4;
        background: #f6fbf7;
    }
}

.confirm-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
}
</style>
